import { Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export interface InterviewEmailPreviewProps {
  candidateName: string;
  subject: string;
  onSubjectChange: (value: string) => void;
  introText: string;
  onIntroTextChange: (value: string) => void;
  stageName: string;
  onStageNameChange: (value: string) => void;
  interviewerName: string;
  interviewerEmail?: string;
  durationMinutes: number;
  stageDescription?: string;
  extraBody: string;
  onExtraBodyChange: (value: string) => void;
  buttonText: string;
  onButtonTextChange: (value: string) => void;
  closingText: string;
  onClosingTextChange: (value: string) => void;
}

export function InterviewEmailPreview({
  candidateName,
  subject,
  onSubjectChange,
  introText,
  onIntroTextChange,
  stageName,
  onStageNameChange,
  interviewerName,
  interviewerEmail,
  durationMinutes,
  stageDescription,
  extraBody,
  onExtraBodyChange,
  buttonText,
  onButtonTextChange,
  closingText,
  onClosingTextChange,
}: InterviewEmailPreviewProps) {
  const firstName = candidateName.split(" ")[0];
  return (
    <div className="space-y-2" data-testid="email-editor">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
        <Mail className="w-3.5 h-3.5" />
        <span>Prévia do email (clique nos textos para editar)</span>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs text-muted-foreground">Assunto</Label>
        <Input
          value={subject}
          onChange={(e) => onSubjectChange(e.target.value)}
          className="h-7 text-xs"
          data-testid="input-email-subject"
        />
      </div>

      <div
        className="rounded-lg border border-border overflow-hidden bg-white dark:bg-zinc-950 shadow-sm"
        data-testid="email-preview"
      >
        <div className="px-4 py-3 space-y-2.5 text-xs text-foreground">
          <p className="text-xs">
            Oi <strong>{firstName}</strong>,
          </p>

          <Textarea
            value={introText}
            onChange={(e) => onIntroTextChange(e.target.value)}
            className="text-xs min-h-[36px] resize-none border-dashed bg-transparent p-1.5"
            rows={2}
            data-testid="input-email-intro"
          />

          <div className="bg-muted/50 rounded-md p-3 space-y-1">
            <div className="flex items-center justify-between gap-2">
              <span className="text-muted-foreground text-xs shrink-0">Próxima etapa</span>
              <input
                value={stageName}
                onChange={(e) => onStageNameChange(e.target.value)}
                className="text-xs font-semibold text-right bg-transparent outline-none border-none w-full"
                data-testid="input-email-stage-name"
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground text-xs">Entrevistador</span>
              <span className="font-semibold text-xs">{interviewerName}</span>
            </div>
            {interviewerEmail && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-xs">Email</span>
                <span className="font-semibold text-xs">{interviewerEmail}</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground text-xs">Duração estimada</span>
              <span className="font-semibold text-xs">{durationMinutes} minutos</span>
            </div>
          </div>

          {stageDescription && (
            <div
              className="bg-blue-50 dark:bg-blue-950/30 border-l-4 border-blue-500 rounded-sm p-2.5"
              data-testid="email-preview-stage-description"
            >
              <p className="text-xs font-semibold text-muted-foreground mb-0.5">
                Informações sobre esta etapa
              </p>
              <p className="text-xs text-foreground whitespace-pre-wrap">{stageDescription}</p>
            </div>
          )}

          <div>
            <Label className="text-[10px] text-muted-foreground/60">
              Mensagem adicional (opcional)
            </Label>
            <Textarea
              value={extraBody}
              onChange={(e) => onExtraBodyChange(e.target.value)}
              className="text-xs min-h-[32px] resize-none border-dashed bg-muted/30 p-1.5"
              rows={1}
              data-testid="input-email-extra-body"
            />
          </div>

          <div className="text-center">
            <input
              value={buttonText}
              onChange={(e) => onButtonTextChange(e.target.value)}
              className="inline-block bg-primary text-primary-foreground text-xs font-semibold px-4 py-2 rounded cursor-text text-center outline-none border-none"
              data-testid="input-email-button"
            />
          </div>

          <Textarea
            value={closingText}
            onChange={(e) => onClosingTextChange(e.target.value)}
            className="text-xs min-h-[32px] resize-none border-dashed bg-transparent p-1.5"
            rows={2}
            data-testid="input-email-closing"
          />

          {interviewerEmail && (
            <div
              className="bg-muted/30 rounded-md p-2.5 text-center"
              data-testid="email-preview-contact-interviewer"
            >
              <p className="text-xs text-muted-foreground">
                Qualquer dúvida, entre em contato com <strong>{interviewerName}</strong>:{" "}
                <span className="text-primary underline">{interviewerEmail}</span>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
